import React, { useEffect, useState } from "react";
import { AdminLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

export function AdminSMTP() {
  const [cfg, setCfg] = useState(null);
  const [testTo, setTestTo] = useState("");
  const load = () => api.get("/admin/smtp").then((r) => setCfg(r.data));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setCfg({ ...cfg, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    try {
      const payload = {
        enabled: !!cfg.enabled,
        host: cfg.host || "",
        port: Number(cfg.port || 587),
        username: cfg.username || "",
        password: cfg.password || "",
        from_email: cfg.from_email || "",
        from_name: cfg.from_name || "BINAREX.CO",
        use_tls: !!cfg.use_tls,
        use_ssl: !!cfg.use_ssl,
        imap_enabled: !!cfg.imap_enabled,
        imap_host: cfg.imap_host || "imap.hostinger.com",
        imap_port: Number(cfg.imap_port || 993),
        imap_username: cfg.imap_username || "",
        imap_password: cfg.imap_password || "",
        imap_use_ssl: cfg.imap_use_ssl !== false,
        imap_folder: cfg.imap_folder || "INBOX",
        imap_poll_minutes: Math.max(1, Math.min(60, Number(cfg.imap_poll_minutes || 5))),
      };
      await api.put("/admin/smtp", payload);
      toast.success("SMTP / IMAP saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const fetchNow = async () => {
    try {
      await api.post("/admin/imap/fetch-now");
      toast.success("IMAP fetch triggered");
      setTimeout(load, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fetch failed"); }
  };
  const sendTest = async () => {
    if (!testTo) { toast.error("Enter a test recipient"); return; }
    try {
      await api.post("/admin/smtp/test", { to: testTo });
      toast.success("Test email sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  if (!cfg) return <AdminLayout><div className="text-slate-400">Loading…</div></AdminLayout>;

  return (
    <AdminLayout>
      <h1 className="bx-heading text-3xl font-extrabold">SMTP / Email & IMAP</h1>
      <p className="text-slate-400 text-sm mt-1">Configure outgoing SMTP (for transactional emails) and incoming IMAP (to auto-convert support emails into tickets).</p>

      <div className="bx-card p-6 mt-6 grid grid-cols-2 gap-4 max-w-3xl">
        <div className="col-span-2 bx-heading text-base font-bold">Outgoing — SMTP</div>
        <label className="col-span-2 flex items-center gap-2 text-sm">
          <input data-testid="smtp-enabled" type="checkbox" checked={!!cfg.enabled} onChange={set("enabled")} /> Enabled
        </label>
        <div>
          <label className="text-xs text-slate-400">SMTP Host</label>
          <input data-testid="smtp-host" value={cfg.host || ""} onChange={set("host")} placeholder="smtp.hostinger.com" className="bx-input mt-1" />
        </div>
        <div>
          <label className="text-xs text-slate-400">Port</label>
          <input data-testid="smtp-port" type="number" value={cfg.port || 587} onChange={set("port")} className="bx-input mt-1" />
        </div>
        <div>
          <label className="text-xs text-slate-400">Username</label>
          <input data-testid="smtp-username" value={cfg.username || ""} onChange={set("username")} placeholder="support@binarex.co" className="bx-input mt-1" />
        </div>
        <div>
          <label className="text-xs text-slate-400">Password</label>
          <input data-testid="smtp-password" type="password" value={cfg.password || ""} onChange={set("password")} placeholder="leave blank to keep current" className="bx-input mt-1" />
        </div>
        <div>
          <label className="text-xs text-slate-400">From Email</label>
          <input data-testid="smtp-from-email" value={cfg.from_email || ""} onChange={set("from_email")} placeholder="support@binarex.co" className="bx-input mt-1" />
        </div>
        <div>
          <label className="text-xs text-slate-400">From Name</label>
          <input data-testid="smtp-from-name" value={cfg.from_name || ""} onChange={set("from_name")} placeholder="BINAREX.CO" className="bx-input mt-1" />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!cfg.use_tls} onChange={set("use_tls")} /> Use STARTTLS (port 587)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!cfg.use_ssl} onChange={set("use_ssl")} /> Use SSL/TLS (port 465)
        </label>

        <div className="col-span-2 border-t border-white/10 pt-4 mt-2 bx-heading text-base font-bold">Incoming — IMAP (auto-create tickets)</div>
        <label className="col-span-2 flex items-center gap-2 text-sm">
          <input data-testid="imap-enabled" type="checkbox" checked={!!cfg.imap_enabled} onChange={set("imap_enabled")} /> Poll incoming mailbox and auto-create support tickets
        </label>
        <div>
          <label className="text-xs text-slate-400">IMAP Host</label>
          <input data-testid="imap-host" value={cfg.imap_host || ""} onChange={set("imap_host")} placeholder="imap.hostinger.com" className="bx-input mt-1" />
        </div>
        <div>
          <label className="text-xs text-slate-400">IMAP Port</label>
          <input data-testid="imap-port" type="number" value={cfg.imap_port || 993} onChange={set("imap_port")} className="bx-input mt-1" />
        </div>
        <div>
          <label className="text-xs text-slate-400">IMAP Username</label>
          <input data-testid="imap-username" value={cfg.imap_username || ""} onChange={set("imap_username")} placeholder="support@binarex.co" className="bx-input mt-1" />
        </div>
        <div>
          <label className="text-xs text-slate-400">IMAP Password</label>
          <input data-testid="imap-password" type="password" value={cfg.imap_password || ""} onChange={set("imap_password")} placeholder="leave blank to keep current" className="bx-input mt-1" />
        </div>
        <div>
          <label className="text-xs text-slate-400">Folder</label>
          <input data-testid="imap-folder" value={cfg.imap_folder || "INBOX"} onChange={set("imap_folder")} className="bx-input mt-1" />
        </div>
        <div>
          <label className="text-xs text-slate-400">Poll interval (minutes)</label>
          <input data-testid="imap-poll" type="number" min="1" max="60" value={cfg.imap_poll_minutes || 5} onChange={set("imap_poll_minutes")} className="bx-input mt-1" />
        </div>
        <label className="col-span-2 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={cfg.imap_use_ssl !== false} onChange={set("imap_use_ssl")} /> Use SSL (port 993)
        </label>
        {cfg.imap_last_check_at && (
          <div className="col-span-2 text-[11px] text-slate-500">
            Last poll: <span className="text-purple-300 bx-mono">{new Date(cfg.imap_last_check_at).toLocaleString()}</span> · last UID processed: {cfg.imap_last_uid || 0}
          </div>
        )}

        <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-white/10">
          <button data-testid="imap-fetch-now" onClick={fetchNow} className="bx-btn-ghost"><Lucide.Inbox size={16} /> Fetch now</button>
          <button data-testid="smtp-save" onClick={save} className="bx-btn-primary"><Lucide.Save size={16} /> Save</button>
        </div>
      </div>

      <div className="bx-card p-5 mt-6 max-w-3xl">
        <div className="bx-heading text-lg font-semibold">Send a test email</div>
        <div className="mt-3 flex gap-3">
          <input data-testid="smtp-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className="bx-input flex-1" />
          <button data-testid="smtp-test-send" onClick={sendTest} className="bx-btn-primary"><Lucide.Send size={16} /> Send test</button>
        </div>
        <p className="text-xs text-slate-500 mt-3">For Hostinger: SMTP <code className="text-purple-300">smtp.hostinger.com</code>:465 SSL · IMAP <code className="text-purple-300">imap.hostinger.com</code>:993 SSL. Use your full mailbox email as the username.</p>
      </div>
    </AdminLayout>
  );
}


export function AdminBulkBlog() {
  const [jobs, setJobs] = useState([]);
  const [count, setCount] = useState(100);
  const [running, setRunning] = useState(false);

  const load = () => api.get("/admin/blog/bulk-jobs").then((r) => setJobs(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, []);

  const start = async () => {
    setRunning(true);
    try {
      await api.post("/admin/blog/bulk-generate", { count: Number(count) });
      toast.success(`Started: ${count} posts. Will generate in the background.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRunning(false); }
  };

  return (
    <AdminLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Bulk Blog Generator</h1>
      <p className="text-slate-400 text-sm mt-1">Generate AI-written, SEO-optimized blog posts in bulk. Each post uses GPT to produce a unique title, excerpt, content (600–900 words), and tags.</p>

      <div className="bx-card p-6 mt-6 max-w-2xl">
        <div className="bx-heading text-lg font-semibold">Start a new batch</div>
        <div className="mt-4 flex flex-wrap gap-2">
          {[10, 25, 50, 100, 250, 500, 1000].map((n) => (
            <button key={n} onClick={() => setCount(n)} data-testid={`bulk-count-${n}`} className={`px-3 py-1.5 rounded-full text-xs ${count === n ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white" : "bg-white/5 border border-white/10 text-slate-300"}`}>
              {n} posts
            </button>
          ))}
          <input type="number" value={count} onChange={(e) => setCount(e.target.value)} className="bx-input w-32" />
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button data-testid="bulk-start" onClick={start} disabled={running} className="bx-btn-primary">
            {running ? <Lucide.Loader2 className="animate-spin" size={16} /> : <Lucide.Sparkles size={16} />} Generate {count} posts
          </button>
          <span className="text-xs text-slate-400">Runs in background. Status updates below every 4s.</span>
        </div>
        <p className="text-xs text-amber-300/90 mt-3"><Lucide.AlertCircle size={12} className="inline mr-1" /> Large batches (500–1000) can take 30–90 minutes and consume significant Universal LLM credits.</p>
      </div>

      <div className="mt-8">
        <div className="bx-heading text-lg font-semibold mb-3">Recent Jobs</div>
        <div className="bx-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left p-3">Started</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Progress</th>
                <th className="text-right p-3">Done</th>
                <th className="text-right p-3">Failed</th>
                <th className="text-right p-3">Total</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => {
                const pct = j.total ? Math.round(((j.completed || 0) / j.total) * 100) : 0;
                return (
                  <tr key={j.id} className="border-t border-white/5">
                    <td className="p-3 text-slate-400">{new Date(j.started_at).toLocaleString()}</td>
                    <td className="p-3"><span className="bx-chip" style={{ fontSize: 10 }}>{j.status}</span></td>
                    <td className="p-3 w-1/3">
                      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-purple-500 to-blue-500" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1">{pct}%</div>
                    </td>
                    <td className="p-3 text-right font-mono text-emerald-400">{j.completed || 0}</td>
                    <td className="p-3 text-right font-mono text-rose-400">{j.failed || 0}</td>
                    <td className="p-3 text-right font-mono">{j.total}</td>
                  </tr>
                );
              })}
              {!jobs.length && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No bulk jobs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
